import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Phone, Clock, Mail, Loader2, ArrowRight, ShieldCheck } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

/**
 * Quick Quote — single-step B2B enquiry form.
 *
 * Posts to the existing /api/contact endpoint and packages the structured
 * property details into the `message` field so they land in the same admin
 * inbox + email pipeline as other contact enquiries.
 *
 * Designed for commercial / SAP / large-project prospects who are NOT ready
 * to commit to a calendar slot in the standard booking flow.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  'Commercial EPC (NDEPC)',
  'SAP Calculation (new build / extension / conversion)',
  'SBEM Calculation',
  'MEES Commercial Advice Report',
  'U-Value Calculations',
  'Part O Overheating Assessment',
  'Multi-project / portfolio enquiry',
  'Other (please specify in message)',
];

const PROPERTY_TYPES = [
  'Office',
  'Retail / shop',
  'Warehouse / industrial',
  'Hotel / B&B',
  'Restaurant / pub / café',
  'New-build dwelling',
  'Conversion / change of use',
  'Mixed-use / portfolio',
  'Other',
];

const QuickQuote = () => {
  // Capture page-load timestamp for spam protection (matches existing /api/contact contract)
  const formTimestamp = useMemo(() => Date.now(), []);

  const [form, setForm] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    service: '',
    propertyType: '',
    floorArea: '',
    postcode: '',
    timeline: '',
    notes: '',
  });
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.service) {
      setStatus('error');
      setErrorMsg('Please complete the required fields (marked *).');
      return;
    }

    setStatus('submitting');
    setErrorMsg('');

    // Build a structured message for the business inbox
    const messageLines = [
      '--- QUICK QUOTE REQUEST ---',
      `Service: ${form.service}`,
      `Property type: ${form.propertyType || '(not specified)'}`,
      `Approx floor area: ${form.floorArea || '(not specified)'}`,
      `Postcode / location: ${form.postcode || '(not specified)'}`,
      `Timeline: ${form.timeline || '(not specified)'}`,
      `Company: ${form.company || '(not specified)'}`,
      '',
      'Additional notes from client:',
      form.notes || '(none)',
    ];

    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        message: messageLines.join('\n'),
        _t: formTimestamp,
      });
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setErrorMsg(err?.response?.data?.detail || 'Sorry, we could not submit your request right now. Please try again or call 01392 980077.');
    }
  };

  if (status === 'success') {
    return (
      <>
        <Helmet>
          <title>Quote Request Received | Energy Performance Direct</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
          <Navbar />
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-2xl mx-auto text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <h1 className="text-4xl font-bold text-gray-900 mb-4">Thanks &mdash; your quote request is on its way.</h1>
                <p className="text-lg text-gray-700 mb-6">
                  We&rsquo;ve received your enquiry and will reply by email within <strong>one working day</strong> with a fixed quote and indicative timeline.
                </p>
                <p className="text-base text-gray-600 mb-8">
                  If your project is urgent, please call <a href="tel:01392980077" className="text-green-600 font-semibold hover:underline">01392 980077</a> and we&rsquo;ll fast-track it.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Link to="/case-studies">
                    <Button variant="outline" className="border-2 border-green-600 text-green-600 px-6 py-5 rounded-full">Browse case studies</Button>
                  </Link>
                  <Link to="/">
                    <Button className="bg-gradient-to-r from-green-600 to-cyan-500 text-white px-6 py-5 rounded-full">Back to home</Button>
                  </Link>
                </div>
              </div>
            </div>
          </section>
          <Footer />
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Quick Quote | Commercial EPC, SAP, SBEM &amp; MEES | Energy Performance Direct</title>
        <meta name="description" content="Request a fast, no-commitment quote for Commercial EPC, SAP, SBEM, MEES advice or multi-project work. Reply within one working day. No calendar booking required." />
        <link rel="canonical" href="https://energyperformancedirect.co.uk/quick-quote" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
        <Navbar />

        {/* Hero */}
        <section className="py-12 bg-gradient-to-br from-green-50 via-cyan-50 to-green-50">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <div className="inline-block mb-4 px-4 py-2 bg-white rounded-full shadow-sm">
                <span className="text-sm font-medium text-green-600">For commercial, SAP &amp; multi-project enquiries</span>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-3">Get a Quick Quote &mdash; no commitment</h1>
              <p className="text-lg text-gray-700">
                Tell us about your project and we&rsquo;ll reply with a fixed price and indicative timeline within one working day. No calendar slot, no payment &mdash; just the numbers you need to plan.
              </p>
            </div>
          </div>
        </section>

        {/* Trust strip */}
        <section className="py-8 bg-white border-b border-gray-100">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
              <div className="flex items-center justify-center gap-2 text-sm text-gray-700">
                <Clock className="w-5 h-5 text-green-600" /> Reply within 1 working day
              </div>
              <div className="flex items-center justify-center gap-2 text-sm text-gray-700">
                <ShieldCheck className="w-5 h-5 text-green-600" /> NDEA &amp; SAP accredited
              </div>
              <div className="flex items-center justify-center gap-2 text-sm text-gray-700">
                <CheckCircle className="w-5 h-5 text-green-600" /> No card details required
              </div>
            </div>
          </div>
        </section>

        {/* Form */}
        <section className="py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <Card className="border-none shadow-xl">
                <CardContent className="p-8">
                  <form onSubmit={handleSubmit} className="space-y-5" data-testid="quick-quote-form">
                    {/* Honeypot timestamp field handled via state; not visible */}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Your name <span className="text-red-500">*</span></label>
                        <input type="text" required value={form.name} onChange={update('name')} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500" data-testid="qq-name" />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Company / practice</label>
                        <input type="text" value={form.company} onChange={update('company')} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500" data-testid="qq-company" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
                        <input type="email" required value={form.email} onChange={update('email')} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500" data-testid="qq-email" />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Phone (optional)</label>
                        <input type="tel" value={form.phone} onChange={update('phone')} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500" data-testid="qq-phone" />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Service you need <span className="text-red-500">*</span></label>
                      <select required value={form.service} onChange={update('service')} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 bg-white" data-testid="qq-service">
                        <option value="">Choose a service&hellip;</option>
                        {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Property type</label>
                        <select value={form.propertyType} onChange={update('propertyType')} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 bg-white" data-testid="qq-property-type">
                          <option value="">Choose&hellip;</option>
                          {PROPERTY_TYPES.map((p) => <option key={p} value={p}>{p}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Approx. floor area (m²)</label>
                        <input type="text" placeholder="e.g. 450" value={form.floorArea} onChange={update('floorArea')} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500" data-testid="qq-area" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Postcode / nearest town</label>
                        <input type="text" placeholder="e.g. EX1, Plymouth, TR8" value={form.postcode} onChange={update('postcode')} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500" data-testid="qq-postcode" />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Timeline</label>
                        <select value={form.timeline} onChange={update('timeline')} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 bg-white" data-testid="qq-timeline">
                          <option value="">Choose&hellip;</option>
                          <option>Urgent — this week</option>
                          <option>Within 2 weeks</option>
                          <option>Within 1 month</option>
                          <option>1–3 months</option>
                          <option>Just exploring</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Anything else we should know?</label>
                      <textarea rows={4} value={form.notes} onChange={update('notes')} placeholder="Brief description of the project, key constraints, multi-site, etc." className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500" data-testid="qq-notes" />
                    </div>

                    {status === 'error' && (
                      <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3" data-testid="qq-error">
                        {errorMsg}
                      </div>
                    )}

                    <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center pt-2">
                      <Button type="submit" disabled={status === 'submitting'} className="bg-gradient-to-r from-green-600 to-cyan-500 hover:from-green-700 hover:to-cyan-600 text-white px-8 py-6 text-base rounded-full shadow-lg" data-testid="qq-submit">
                        {status === 'submitting' ? (
                          <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Sending&hellip;</>
                        ) : (
                          <>Send my quote request <ArrowRight className="w-4 h-4 ml-2" /></>
                        )}
                      </Button>
                      <p className="text-xs text-gray-500">
                        By submitting, you agree to our <Link to="/privacy" className="text-green-600 hover:underline">privacy policy</Link>. No card details required.
                      </p>
                    </div>
                  </form>
                </CardContent>
              </Card>

              {/* Alternative contact */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
                <a href="tel:01392980077" className="flex items-center gap-3 p-4 bg-white border border-gray-200 rounded-xl hover:shadow-md transition-all">
                  <Phone className="w-6 h-6 text-green-600" />
                  <div>
                    <div className="font-semibold text-gray-900">Prefer to talk it through?</div>
                    <div className="text-sm text-green-600">01392 980077</div>
                  </div>
                </a>
                <a href="mailto:info@energyperformancedirect.co.uk" className="flex items-center gap-3 p-4 bg-white border border-gray-200 rounded-xl hover:shadow-md transition-all">
                  <Mail className="w-6 h-6 text-green-600" />
                  <div>
                    <div className="font-semibold text-gray-900">Or email us direct</div>
                    <div className="text-sm text-green-600">info@energyperformancedirect.co.uk</div>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default QuickQuote;
